// A material focused in the right panel: THE detail view of one material,
// meant to be shared with Bibliotek (D35), so it depends on props only, never
// on Kontext state. The material's quiet header, its lenses ("Is it
// grounded?" / "Does it teach well?", plus "Does it meet the goals?" when it is
// read against a chat's goals: `goal`) each with today's answer, and below
// them either the material itself (with grounding + heavy-text marks in its
// margin) or one lens's findings. Every finding links both ways: to its place in the
// material, and (grounding) to the claim's source passage.
//
// Projection in: the material's quality read is the panel's ONE live read
// (useMaterialQualities), so the lenses update when a turn adds citations.
// The content is read through the port seam. Local state: which lens is open
// (view-ephemeral; it resets when the panel is pointed at another element).

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type { ClaimIntents } from "../../../components/ClaimRow/ClaimRow.js";
import { MaterialDocument } from "../../../components/Editor/MaterialDocument/MaterialDocument.js";
import { defaultRenderers } from "../../../components/Editor/renderers.js";
import { FactCheckPanel } from "../../../components/FactCheckPanel/FactCheckPanel.js";
import { MaterialHeader } from "../../../components/MaterialPreview/MaterialPreview.js";
import {
    elementFocusTarget,
    focusElementOf,
} from "../../../components/Navigation/focusTarget.js";
import type {
    FocusTarget,
    MaterialRef,
    ResourceRef,
} from "../../../components/Navigation/types.js";
import { PedagogicalQuality } from "../../../components/PedagogicalQuality/PedagogicalQuality.js";
import {
    type MaterialQuality,
    type StatementSource,
    statementSourcesOf,
} from "../../../components/QualitySummary/QualitySummary.js";
import type { LoadedMaterial } from "../../ports/resource.js";
import type { AppPorts } from "../../ports.js";
import { LoadView, usePortLoad } from "../ResourceDetail.js";
import {
    factCheckModel,
    pedagogicalQualityModel,
} from "../reviewQualityModel.js";
import {
    type GoalIntents,
    GoalLens,
    goalLensAnswer,
    goalLensRead,
} from "./GoalViews.js";
import { type ChatGoals, toneOfMaterial } from "./goal.js";
import { LensBar, type LensId, Trail } from "./LensBar.js";
import { annotationsOf } from "./materialMarks.js";

type MaterialLens =
    | { readonly type: "document"; readonly reveal: string | undefined }
    | { readonly type: "grounding" }
    | { readonly type: "teaching" }
    | { readonly type: "goal" };

// The chat's goals this material is read against (the third lens), with the
// lens's intents. Absent = no goal lens (the detail view outside a chat).
export type MaterialGoalLens = {
    readonly goals: ChatGoals;
    readonly intents: GoalIntents;
    readonly onSetGoal?: () => void;
};

export type MaterialFocusProps = {
    readonly material: MaterialRef;
    readonly quality: MaterialQuality | null;
    // Where to open: an element to reveal (a claim the author came back to).
    readonly target: FocusTarget | undefined;
    readonly loadMaterial: AppPorts["loadMaterial"];
    // The host's re-read world: the content is read again when it changes.
    readonly reloadKey: unknown;
    // The generation side view when the material is still being made, else null.
    readonly generation: ReactNode | null;
    readonly onShowStatementSource: (row: StatementSource) => void;
    // A backed statement's claims in Fact check: open, correct, remove.
    readonly claimIntents?: ClaimIntents;
    readonly onOpenRef: (ref: ResourceRef) => void;
    readonly goal?: MaterialGoalLens;
};

export const MaterialFocus = ({
    material,
    quality,
    target,
    loadMaterial,
    reloadKey,
    generation,
    onShowStatementSource,
    claimIntents,
    onOpenRef,
    goal,
}: MaterialFocusProps): ReactNode => {
    const { t, i18n } = useLingui();
    const [lens, setLens] = useState<MaterialLens>({
        type: "document",
        reveal: focusElementOf(target),
    });
    const { load, retry } = usePortLoad(loadMaterial, material.id, reloadKey);
    const factModel = factCheckModel(quality);
    const pedModel = pedagogicalQualityModel(quality);
    const rows = statementSourcesOf(quality);
    const showAt = (id: string) => setLens({ type: "document", reveal: id });
    const activeLens: LensId | null =
        lens.type === "document" ? null : lens.type;
    const back = (
        <Trail
            label={t`Back to the material`}
            onBack={() => setLens({ type: "document", reveal: undefined })}
        />
    );
    const locatorOf = (row: StatementSource): string =>
        row.page === undefined ? row.source.title : t`p. ${row.page}`;

    const document = (loaded: LoadedMaterial): ReactNode => (
        <>
            <MaterialHeader
                title={material.title}
                provenance={loaded.provenance}
                onOpenSource={onOpenRef}
            />
            {generation ?? (
                <MaterialDocument
                    elements={loaded.elements}
                    renderers={defaultRenderers}
                    annotations={annotationsOf(quality, locatorOf)}
                    ariaLabel={t`Material content`}
                    revealTarget={
                        lens.type === "document" && lens.reveal !== undefined
                            ? elementFocusTarget(lens.reveal)
                            : undefined
                    }
                    onMarkerActivate={(id, marker) => {
                        if (marker.type === "issue")
                            return setLens({ type: "teaching" });
                        const row = rows.find((r) => r.statementId === id);
                        return row === undefined
                            ? setLens({ type: "grounding" })
                            : onShowStatementSource(row);
                    }}
                />
            )}
        </>
    );

    const body = ((): ReactNode => {
        switch (lens.type) {
            case "document":
                return (
                    <div className="bilda-rp-document">
                        <LoadView
                            load={load}
                            kind="material"
                            onRetry={retry}
                            render={document}
                        />
                    </div>
                );
            case "grounding":
                return (
                    <div className="bilda-rp-lensbody">
                        {back}
                        <FactCheckPanel
                            model={factModel}
                            onOpenItem={showAt}
                            {...(claimIntents === undefined
                                ? {}
                                : { claimIntents })}
                        />
                    </div>
                );
            case "teaching":
                return (
                    <div className="bilda-rp-lensbody">
                        {back}
                        <PedagogicalQuality
                            model={pedModel}
                            onOpenSection={showAt}
                        />
                    </div>
                );
            case "goal":
                return goal === undefined ? null : (
                    <div className="bilda-rp-lensbody">
                        {back}
                        <GoalLens
                            goals={goal.goals}
                            materialId={material.id}
                            intents={{
                                ...goal.intents,
                                // A part of THIS material reveals in place.
                                onOpenPart: (part) =>
                                    part.materialId === material.id
                                        ? showAt(part.elementId)
                                        : goal.intents.onOpenPart(part),
                            }}
                            {...(goal.onSetGoal === undefined
                                ? {}
                                : { onSetGoal: goal.onSetGoal })}
                        />
                    </div>
                );
        }
    })();
    const goalBar =
        goal === undefined
            ? undefined
            : {
                  answer: goalLensAnswer(
                      goal.goals.length > 0,
                      goalLensRead(goal.goals, material.id),
                  )
                      .map((d) => i18n._(d))
                      .join(". "),
                  tone:
                      goal.goals.length === 0
                          ? "pending"
                          : toneOfMaterial(
                                goalLensRead(goal.goals, material.id),
                            ),
              };

    return (
        <>
            <LensBar
                factCheck={factModel}
                pedagogy={pedModel}
                {...(goalBar === undefined ? {} : { goal: goalBar })}
                active={activeLens}
                onToggle={(next) =>
                    setLens(
                        activeLens === next
                            ? { type: "document", reveal: undefined }
                            : { type: next },
                    )
                }
            />
            {body}
        </>
    );
};
